import type { Dispatch, RefObject } from 'react';
import {
  DEFAULT_ARTIFACT_NAME,
  DEFAULT_FIELD_KEY,
  DEFAULT_QUESTION_KEY,
  nodeKeyError,
  takenOutputKeys,
  uniqueArtifactKey,
} from '../store/field-key';
import type { Action, State } from '../store/reducer';
import type {
  Artifact,
  DecisionLevel,
  DecisionNode,
  DecisionOption,
  DecisionQuestion,
  DecisionQuestionYesNo,
  DecisionStateRef,
  Field,
  RuleCondition,
  RuleGroup,
  RulesNode,
  Task,
} from '../store/types';
import { isDecisionNode, isRulesNode, isTaskNode } from '../store/types';
import { availableKey } from '../store/utils';
import { nodeToWire } from '../store/wire';
import type { MutateOp } from './use-builder-mutation';

export type TasksActions = {
  updateTask: (taskId: string, patch: Partial<Pick<Task, 'name' | 'prompt'>>) => void;
  /** Applies `updater` to the task as it stands, not as the caller last rendered it. */
  editTask: (taskId: string, updater: (task: Task) => Task, options?: { save?: boolean }) => void;
  addField: (taskId: string, newId: string) => void;
  updateField: (taskId: string, fieldId: string, patch: Omit<Partial<Field>, 'id'>) => void;
  removeField: (taskId: string, fieldId: string) => void;
  addArtifact: (taskId: string, initial?: Partial<Pick<Artifact, 'name' | 'spec'>>) => string | null;
  updateArtifact: (
    taskId: string,
    artifactId: string,
    patch: Omit<Partial<Artifact>, 'id'>,
    options?: { save?: boolean },
  ) => void;
  removeArtifact: (taskId: string, artifactId: string) => void;
};

export type RulesActions = {
  updateNode: (nodeId: string, patch: Partial<Pick<RulesNode, 'name' | 'key' | 'defaultValue'>>) => void;
  addGroup: (nodeId: string, newId: string) => void;
  updateGroup: (nodeId: string, groupId: string, patch: Partial<Pick<RuleGroup, 'value'>>) => void;
  removeGroup: (nodeId: string, groupId: string) => void;
  moveGroup: (nodeId: string, groupId: string, direction: 'up' | 'down') => void;
  addCondition: (nodeId: string, groupId: string, newId: string) => void;
  updateCondition: (nodeId: string, groupId: string, conditionId: string, expression: string) => void;
  removeCondition: (nodeId: string, groupId: string, conditionId: string) => void;
};

type QuestionPatch = Partial<Pick<DecisionQuestionYesNo, 'key' | 'prompt' | 'yesMeans' | 'noMeans' | 'threshold'>>;

export type DecisionActions = {
  updateNode: (nodeId: string, patch: Partial<Pick<DecisionNode, 'name' | 'context' | 'model'>>) => void;
  setStateRefs: (nodeId: string, refs: DecisionStateRef[]) => void;
  addQuestion: (nodeId: string, kind: DecisionQuestion['kind'], newId: string) => void;
  updateQuestion: (nodeId: string, questionId: string, patch: QuestionPatch) => void;
  removeQuestion: (nodeId: string, questionId: string) => void;
  moveQuestion: (nodeId: string, questionId: string, direction: 'up' | 'down') => void;
  addOption: (nodeId: string, questionId: string, newId: string) => void;
  updateOption: (
    nodeId: string,
    questionId: string,
    optionId: string,
    patch: Omit<Partial<DecisionOption>, 'id'>,
  ) => void;
  removeOption: (nodeId: string, questionId: string, optionId: string) => void;
  addLevel: (nodeId: string, questionId: string, newId: string) => void;
  updateLevel: (nodeId: string, questionId: string, levelId: string, description: string) => void;
  removeLevel: (nodeId: string, questionId: string, levelId: string) => void;
};

export type NodesActions = { tasks: TasksActions; rules: RulesActions; decision: DecisionActions };

type Args = {
  stateRef: RefObject<State>;
  dispatch: Dispatch<Action>;
  mutateOp: MutateOp;
};

const blankField = (id: string): Field => {
  return { id, name: DEFAULT_FIELD_KEY, type: 'text', prompt: '' };
};
const blankArtifact = (id: string): Artifact => {
  return { id, name: DEFAULT_ARTIFACT_NAME, type: 'markdown', spec: '', isStarred: false };
};
const blankCondition = (id: string): RuleCondition => {
  return { id, expression: '' };
};
const blankGroup = (id: string): RuleGroup => {
  return { id, value: '', conditions: [blankCondition(`${id}_c0`)] };
};
const blankOption = (id: string, value: string): DecisionOption => {
  return { id, value, description: '' };
};
const unusedOptionValue = (options: DecisionOption[]): string => {
  const taken = new Set(options.map(option => option.value.trim()));
  let n = options.length + 1;
  while (taken.has(`option_${n}`)) n += 1;
  return `option_${n}`;
};
const blankLevel = (id: string, description: string): DecisionLevel => {
  return { id, description };
};
// Two named options (or levels) so the server's "at least two" check passes on the immediate save.
const blankQuestion = (kind: DecisionQuestion['kind'], id: string, key: string): DecisionQuestion => {
  if (kind === 'yes_no') {
    return { id, kind, key, prompt: '', yesMeans: '', noMeans: '', threshold: '0.5' };
  }
  if (kind === 'score') {
    const levels = [blankLevel(`${id}_l0`, 'Low'), blankLevel(`${id}_l1`, 'High')];
    return { id, kind, key, prompt: '', levels };
  }
  const options = [blankOption(`${id}_o0`, 'option_a'), blankOption(`${id}_o1`, 'option_b')];
  return { id, kind, key, prompt: '', options };
};

const mapQuestion = (
  node: DecisionNode,
  questionId: string,
  updater: (question: DecisionQuestion) => DecisionQuestion,
): DecisionNode => ({
  ...node,
  questions: node.questions.map(question => (question.id === questionId ? updater(question) : question)),
});

const mapOptions = (
  node: DecisionNode,
  questionId: string,
  updater: (options: DecisionOption[]) => DecisionOption[],
): DecisionNode =>
  mapQuestion(node, questionId, question =>
    question.kind === 'pick_one' ? { ...question, options: updater(question.options) } : question,
  );

const mapLevels = (
  node: DecisionNode,
  questionId: string,
  updater: (levels: DecisionLevel[]) => DecisionLevel[],
): DecisionNode =>
  mapQuestion(node, questionId, question =>
    question.kind === 'score' ? { ...question, levels: updater(question.levels) } : question,
  );

const buildNodesActions = ({ stateRef, dispatch, mutateOp }: Args): NodesActions => {
  const editTask = (taskId: string, updater: (task: Task) => Task) => {
    dispatch({ type: 'updateTask', taskId, updater });
  };
  const editRules = (nodeId: string, updater: (node: RulesNode) => RulesNode) => {
    dispatch({ type: 'updateRulesNode', nodeId, updater });
  };
  const editDecision = (nodeId: string, updater: (node: DecisionNode) => DecisionNode) => {
    dispatch({ type: 'updateDecisionNode', nodeId, updater });
  };

  // Structural ops must save immediately. A user who clicks "Add field" and
  // closes the tab before blurring out would otherwise lose the new row —
  // the wrapper-blur path it depends on never fires.
  const saveNodeIfValid = (next: Task | RulesNode | DecisionNode) => {
    if (!nodeKeyError(next)) mutateOp({ kind: 'update_node', node: nodeToWire(next) });
  };
  const editTaskAndSave = (taskId: string, updater: (task: Task) => Task) => {
    const task = stateRef.current.bundle.nodes.find(node => isTaskNode(node) && node.id === taskId);
    if (!task || !isTaskNode(task)) return;
    const next = updater(task);
    dispatch({ type: 'updateTask', taskId, updater: () => next });
    saveNodeIfValid(next);
  };
  const editRulesAndSave = (nodeId: string, updater: (node: RulesNode) => RulesNode) => {
    const rulesNode = stateRef.current.bundle.nodes.find(node => isRulesNode(node) && node.id === nodeId);
    if (!rulesNode || !isRulesNode(rulesNode)) return;
    const next = updater(rulesNode);
    dispatch({ type: 'updateRulesNode', nodeId, updater: () => next });
    saveNodeIfValid(next);
  };
  const editDecisionAndSave = (nodeId: string, updater: (node: DecisionNode) => DecisionNode) => {
    const decisionNode = stateRef.current.bundle.nodes.find(node => isDecisionNode(node) && node.id === nodeId);
    if (!decisionNode || !isDecisionNode(decisionNode)) return;
    const next = updater(decisionNode);
    dispatch({ type: 'updateDecisionNode', nodeId, updater: () => next });
    saveNodeIfValid(next);
  };

  return {
    tasks: {
      updateTask: (taskId, patch) => {
        editTask(taskId, task => {
          return { ...task, ...patch };
        });
      },
      editTask: (taskId, updater, { save = false } = {}) => {
        if (save) editTaskAndSave(taskId, updater);
        else editTask(taskId, updater);
      },
      addField: (taskId, newId) => {
        editTaskAndSave(taskId, task => {
          return { ...task, fields: [...task.fields, blankField(newId)] };
        });
      },
      updateField: (taskId, fieldId, patch) => {
        editTask(taskId, task => {
          return {
            ...task,
            fields: task.fields.map(field => (field.id === fieldId ? { ...field, ...patch } : field)),
          };
        });
      },
      removeField: (taskId, fieldId) => {
        editTaskAndSave(taskId, task => {
          return { ...task, fields: task.fields.filter(field => field.id !== fieldId) };
        });
      },
      addArtifact: (taskId, initial) => {
        if (!stateRef.current.bundle.nodes.some(node => isTaskNode(node) && node.id === taskId)) return null;
        const newId = uniqueArtifactKey(DEFAULT_ARTIFACT_NAME, takenOutputKeys(stateRef.current.bundle));
        editTaskAndSave(taskId, task => ({
          ...task,
          artifacts: [...task.artifacts, { ...blankArtifact(newId), ...initial }],
        }));
        return newId;
      },
      updateArtifact: (taskId, artifactId, patch, { save = false } = {}) => {
        const updater = (task: Task) => ({
          ...task,
          artifacts: task.artifacts.map(artifact =>
            artifact.id === artifactId ? { ...artifact, ...patch } : artifact,
          ),
        });
        if (save) editTaskAndSave(taskId, updater);
        else editTask(taskId, updater);
      },
      removeArtifact: (taskId, artifactId) => {
        editTaskAndSave(taskId, task => {
          return { ...task, artifacts: task.artifacts.filter(artifact => artifact.id !== artifactId) };
        });
      },
    },
    rules: {
      updateNode: (nodeId, patch) => {
        editRules(nodeId, node => {
          return { ...node, ...patch };
        });
      },
      addGroup: (nodeId, newId) => {
        editRulesAndSave(nodeId, node => {
          return { ...node, groups: [...node.groups, blankGroup(newId)] };
        });
      },
      updateGroup: (nodeId, groupId, patch) => {
        editRules(nodeId, node => {
          return {
            ...node,
            groups: node.groups.map(group => (group.id === groupId ? { ...group, ...patch } : group)),
          };
        });
      },
      removeGroup: (nodeId, groupId) => {
        editRulesAndSave(nodeId, node => {
          return { ...node, groups: node.groups.filter(group => group.id !== groupId) };
        });
      },
      moveGroup: (nodeId, groupId, direction) => {
        editRulesAndSave(nodeId, node => {
          const idx = node.groups.findIndex(group => group.id === groupId);
          const target = direction === 'up' ? idx - 1 : idx + 1;
          if (idx === -1 || target < 0 || target >= node.groups.length) return node;
          const groups = [...node.groups];
          [groups[idx], groups[target]] = [groups[target], groups[idx]];
          return { ...node, groups };
        });
      },
      addCondition: (nodeId, groupId, newId) => {
        editRulesAndSave(nodeId, node => {
          return {
            ...node,
            groups: node.groups.map(group => {
              if (group.id !== groupId) return group;
              return { ...group, conditions: [...group.conditions, blankCondition(newId)] };
            }),
          };
        });
      },
      updateCondition: (nodeId, groupId, conditionId, expression) => {
        editRules(nodeId, node => {
          return {
            ...node,
            groups: node.groups.map(group => {
              if (group.id !== groupId) return group;
              return {
                ...group,
                conditions: group.conditions.map(condition =>
                  condition.id === conditionId ? { ...condition, expression } : condition,
                ),
              };
            }),
          };
        });
      },
      removeCondition: (nodeId, groupId, conditionId) => {
        editRulesAndSave(nodeId, node => {
          return {
            ...node,
            groups: node.groups.map(group => {
              if (group.id !== groupId) return group;
              return { ...group, conditions: group.conditions.filter(condition => condition.id !== conditionId) };
            }),
          };
        });
      },
    },
    decision: {
      updateNode: (nodeId, patch) => {
        editDecision(nodeId, node => ({ ...node, ...patch }));
      },
      setStateRefs: (nodeId, refs) => {
        editDecisionAndSave(nodeId, node => ({ ...node, state: refs }));
      },
      addQuestion: (nodeId, kind, newId) => {
        const key = availableKey(DEFAULT_QUESTION_KEY, takenOutputKeys(stateRef.current.bundle));
        editDecisionAndSave(nodeId, node => ({
          ...node,
          questions: [...node.questions, blankQuestion(kind, newId, key)],
        }));
      },
      updateQuestion: (nodeId, questionId, patch) => {
        editDecision(nodeId, node => mapQuestion(node, questionId, question => ({ ...question, ...patch })));
      },
      removeQuestion: (nodeId, questionId) => {
        editDecisionAndSave(nodeId, node => ({
          ...node,
          questions: node.questions.filter(question => question.id !== questionId),
        }));
      },
      moveQuestion: (nodeId, questionId, direction) => {
        editDecisionAndSave(nodeId, node => {
          const idx = node.questions.findIndex(question => question.id === questionId);
          const target = direction === 'up' ? idx - 1 : idx + 1;
          if (idx === -1 || target < 0 || target >= node.questions.length) return node;
          const questions = [...node.questions];
          [questions[idx], questions[target]] = [questions[target], questions[idx]];
          return { ...node, questions };
        });
      },
      addOption: (nodeId, questionId, newId) => {
        editDecisionAndSave(nodeId, node =>
          mapOptions(node, questionId, options => [...options, blankOption(newId, unusedOptionValue(options))]),
        );
      },
      updateOption: (nodeId, questionId, optionId, patch) => {
        editDecision(nodeId, node =>
          mapOptions(node, questionId, options =>
            options.map(option => (option.id === optionId ? { ...option, ...patch } : option)),
          ),
        );
      },
      removeOption: (nodeId, questionId, optionId) => {
        editDecisionAndSave(nodeId, node =>
          mapOptions(node, questionId, options => options.filter(option => option.id !== optionId)),
        );
      },
      addLevel: (nodeId, questionId, newId) => {
        editDecisionAndSave(nodeId, node =>
          mapLevels(node, questionId, levels => [...levels, blankLevel(newId, `Level ${levels.length + 1}`)]),
        );
      },
      updateLevel: (nodeId, questionId, levelId, description) => {
        editDecision(nodeId, node =>
          mapLevels(node, questionId, levels =>
            levels.map(level => (level.id === levelId ? { ...level, description } : level)),
          ),
        );
      },
      removeLevel: (nodeId, questionId, levelId) => {
        editDecisionAndSave(nodeId, node =>
          mapLevels(node, questionId, levels => levels.filter(level => level.id !== levelId)),
        );
      },
    },
  };
};

export { unusedOptionValue };
export default buildNodesActions;
